import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Input, Button } from '@qodo/design-system';
import { EMAIL_REGEX } from 'consts/email';
import { KEYBOARD_KEYS } from 'consts/keyboard';
import { ArrowRight } from 'lucide-react';
import { ROUTES } from 'routes/consts';
import { mixpanelService } from 'services/mixpanel';
import {
  MIXPANEL_EVENT_NAMES,
  MIXPANEL_OPERATIONS,
} from 'services/mixpanel/consts';
import { MixpanelOperationName } from 'services/mixpanel/types';
import { isAirgappedMode } from 'utils/env';
import { spawn } from 'utils/translations';
import { Title } from './Title';

type EmailInputFormProps = {
  type: 'private' | 'sso';
  onSubmit: (email: string) => Promise<void>;
};

export const EmailInputForm = ({
  type,
  onSubmit: propsOnSubmit,
}: EmailInputFormProps) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const spawnText = spawn(`login.${type}`);
  const isEmailEmpty = !email || email.length === 0;

  const onDifferentSignupMethodClick = () => {
    mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
      operation: MIXPANEL_OPERATIONS.CHOOSE_DIFFERENT_SIGNUP_METHOD,
    });

    navigate(ROUTES.SIGNIN);
  };

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);

    if (!e.target.value) {
      setError(undefined);
    }
  };

  const onSubmit = async () => {
    const isEmailValid = EMAIL_REGEX.test(email);

    if (!isEmailValid) {
      setError(t('form.validations.invalid-email', { email }));

      return;
    }

    setError(undefined);
    setIsLoading(true);

    try {
      let operation: MixpanelOperationName = MIXPANEL_OPERATIONS.SUBMIT_EMAIL;

      if (type === 'private') {
        operation = MIXPANEL_OPERATIONS.SUBMIT_EMAIL;
      }

      if (type === 'sso') {
        operation = MIXPANEL_OPERATIONS.SUBMIT_SSO;
      }

      mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
        operation,
      });

      await propsOnSubmit(email);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isEmailEmpty) {
      return;
    }

    const shouldSubmit = e.key === KEYBOARD_KEYS.ENTER;

    if (shouldSubmit) {
      onSubmit();
    }
  };

  const renderDifferentSignupMethodButtonIfNeeded = () => {
    if (isAirgappedMode()) {
      return <></>;
    }

    return (
      <Button variant="link" onClick={onDifferentSignupMethodClick}>
        {spawnText('different-signup-method-button')}
      </Button>
    );
  };

  return (
    <div className="flex flex-col gap-9 max-w-md">
      <Title className="text-start" description={spawnText('description')}>
        {spawnText('title')}
      </Title>

      <div className="flex flex-col gap-7">
        <div className="w-full flex gap-2 flex-col items-start">
          <div className="focus-within:ring-1 focus-within:ring-purple-700 flex gap-2 items-center w-full bg-fill-panel-neutral border-border-input-neutral-idle px-3 rounded-md">
            <Input
              className="w-full bg-transparent border-0 placeholder:text-text-input-neutral-placeholder px-0"
              placeholder={spawnText('email-placeholder')}
              value={email}
              onChange={onEmailChange}
              onKeyDown={onKeyDown}
              autoFocus
            />
            <Button
              variant="ghost"
              onClick={onSubmit}
              loading={isLoading}
              disabled={isEmailEmpty}
            >
              {!isLoading && (
                <ArrowRight className="size-5 stroke-text-general-neutral-mid" />
              )}
            </Button>
          </div>
          {error && (
            <p className="text-text-action-negative-idle text-sm mt-1">
              {error}
            </p>
          )}
        </div>
        {renderDifferentSignupMethodButtonIfNeeded()}
      </div>
    </div>
  );
};

export default EmailInputForm;
